import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../config/supabaseClient';
import Button from '../components/ui/Button';
import FormField from '../components/ui/FormField';
import Input from '../components/ui/Input';
import './Auth.css';

function SignUp() {
  const navigate       = useNavigate();
  const [searchParams]  = useSearchParams();
  const rawRedirect    = searchParams.get('redirect')
    ? decodeURIComponent(searchParams.get('redirect'))
    : '/dashboard';
  // Prevent open-redirect attacks — only allow internal paths
  const redirectTo = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//') ? rawRedirect : '/dashboard';

  // Form field state
  const [firstName,       setFirstName]       = useState('');
  const [lastName,        setLastName]        = useState('');
  const [username,        setUsername]        = useState('');
  const [email,           setEmail]           = useState('');
  const [password,        setPassword]        = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Username availability: null | 'checking' | 'available' | 'taken'
  const [usernameStatus, setUsernameStatus] = useState(null);

  // UI state
  const [errors,    setErrors]    = useState({});
  const [authError, setAuthError] = useState('');
  const [loading,   setLoading]   = useState(false);

  // ── Username: enforce lowercase + allowed chars on every keystroke
  function handleUsernameChange(e) {
    const cleaned = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(cleaned);
    setUsernameStatus(null); // reset until debounce fires
  }

  // ── Real-time availability check (debounced 400ms) ──────────
  useEffect(() => {
    if (username.length < 3) { setUsernameStatus(null); return; }

    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', username)
        .maybeSingle();
      setUsernameStatus(data ? 'taken' : 'available');
    }, 400);
    return () => clearTimeout(timer);
  }, [username]);

  // ── Front-end validation ────────────────────────────────────
  function validate() {
    const newErrors = {};
    if (!firstName.trim())
      newErrors.firstName = 'Please enter your first name.';
    if (!lastName.trim())
      newErrors.lastName = 'Please enter your last name.';
    if (!username.trim())
      newErrors.username = 'Please choose a username.';
    else if (username.length < 3)
      newErrors.username = 'Username must be at least 3 characters.';
    else if (username.length > 20)
      newErrors.username = 'Username must be 20 characters or fewer.';
    else if (usernameStatus === 'taken')
      newErrors.username = 'This username is already taken.';
    else if (usernameStatus === 'checking')
      newErrors.username = 'Still checking availability — please wait a moment.';
    if (!email.trim())
      newErrors.email = 'Please enter your email address.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
      newErrors.email = 'Please enter a valid email address.';
    if (password.length < 8)
      newErrors.password = 'Password must be at least 8 characters.';
    if (password !== confirmPassword)
      newErrors.confirmPassword = 'Passwords do not match.';
    return newErrors;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setAuthError('');

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);

    // Store all name fields in auth metadata so Dashboard can copy them
    // into the profiles row on first login.
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: firstName.trim(),
          last_name:  lastName.trim(),
          full_name:  `${firstName.trim()} ${lastName.trim()}`,
          username,
        },
      },
    });

    setLoading(false);

    if (error) {
      setAuthError(error.message);
      return;
    }

    navigate(redirectTo);
  }

  // ── Username status label ───────────────────────────────────
  function UsernameStatus() {
    if (!username || username.length < 3) {
      return <p className="auth-username-hint">Letters, numbers, underscores · 3–20 chars</p>;
    }
    if (usernameStatus === 'checking') {
      return <p className="auth-username-status auth-username-status--checking">Checking…</p>;
    }
    if (usernameStatus === 'available') {
      return <p className="auth-username-status auth-username-status--available">✓ Available</p>;
    }
    if (usernameStatus === 'taken') {
      return <p className="auth-username-status auth-username-status--taken">✗ Already taken</p>;
    }
    return null;
  }

  return (
    <div className="auth-page">
      <div className="auth-card">

        <Link to="/" className="auth-logo">
          Rank<span>TT</span>
        </Link>

        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Start free. No credit card required.</p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>

          {/* First name + Last name side by side */}
          <div className="auth-name-row">
            <FormField label="First name" htmlFor="firstName" error={errors.firstName}>
              <Input
                id="firstName"
                name="firstName"
                type="text"
                placeholder="Jane"
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                autoComplete="given-name"
                error={errors.firstName}
              />
            </FormField>

            <FormField label="Last name" htmlFor="lastName" error={errors.lastName}>
              <Input
                id="lastName"
                name="lastName"
                type="text"
                placeholder="Smith"
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                autoComplete="family-name"
                error={errors.lastName}
              />
            </FormField>
          </div>

          <FormField label="Username" htmlFor="username" error={errors.username}>
            <Input
              id="username"
              name="username"
              type="text"
              placeholder="janesmith"
              value={username}
              onChange={handleUsernameChange}
              autoComplete="username"
              error={errors.username}
              maxLength={20}
            />
            <UsernameStatus />
          </FormField>

          <FormField label="Email address" htmlFor="email" error={errors.email}>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="jane@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              error={errors.email}
            />
          </FormField>

          <FormField label="Password" htmlFor="password" error={errors.password}>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
              error={errors.password}
            />
          </FormField>

          <FormField label="Confirm password" htmlFor="confirmPassword" error={errors.confirmPassword}>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Re-enter your password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              error={errors.confirmPassword}
            />
          </FormField>

          {authError && <p className="auth-error">{authError}</p>}

          <Button type="submit" fullWidth size="lg" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </Button>

        </form>

        <p className="auth-switch">
          Already have an account?{' '}
          <Link to="/login" className="auth-link">Log in &rarr;</Link>
        </p>

        <p className="auth-legal">
          By creating an account you agree to our{' '}
          <Link to="/terms-of-service" className="auth-link">Terms of Service</Link> and{' '}
          <Link to="/privacy-policy" className="auth-link">Privacy Policy</Link>.
        </p>

      </div>
    </div>
  );
}

export default SignUp;
